// Resource.State — three active variants + the deleted wrapper.
//
// Per `.docs/v1/design/bounded-contexts.md` §1 (and Slice 5 widening).
// Three active variants shipped as of Slice 5 (`Source`,
// `Material`, `Export`); Slice 2 landed the first
// two, Slice 5 adds `Export` + `PackagingProvenance` and
// widens `Deleted.prior` additively.
//
// Orthogonal axes carried as *fields* on the active variants
// (not sum discriminants), per the design:
//   - `classification: "standalone" | "embedded"` — flipped by
//     `MakeResourceStandalone` / `MakeResourceEmbedded`.
//   - `phase: "draft" | "confirmed"` — flipped by `ConfirmResource`.
//   - `archivedAt?: VersionMarker` — set by `ArchiveResource`, cleared
//     by `UnarchiveResource`. Absent = not archived. An orthogonal
//     optional field (mirrors `Source.summary?`), NOT a sum variant:
//     archive is a "set aside" overlay on the SAME shape, distinct
//     from the `Deleted` wrapper (removal). Because it rides the active
//     variant, it survives delete/restore for free — `Deleted.prior`
//     stores the whole shape, `RestoreResource` returns to it verbatim.
//     Distinct commands per intent (`Archive`/`Unarchive`), never a
//     boolean toggle (F86; docs/model/principles.md 17).
//
// The **checksum register** — `ReadonlyMap<ContentHash, ResourceId>`
// — lives inside the BC-level `ResourceState` record as an internal
// facet. Consulted by `IngestResource` and refused-on-match. Written
// by the Slice-5 `CreateExportResource` PM path.
export { initial, mkAuthoredProvenance, mkChunkSummary, mkDeleted, mkExport, mkExternalProvenance, mkFile, mkFileDescription, mkMaterial, mkMediaType, mkPackageOriginMaterial, mkPackageOriginUnrecorded, mkPackagingProvenance, mkPromotedFromFileProvenance, mkProvenanceHint, mkResourceState, mkResourceTitle, mkSource, mkSourceBibliography, mkSourceSummary, };
const mkResourceTitle = (s) => s;
const mkProvenanceHint = (s) => s;
const mkMediaType = (s) => s;
const mkFileDescription = (s) => s;
const mkExternalProvenance = (inputKind, hint) => ({ inputKind, hint });
const mkAuthoredProvenance = (authoredAt, authorship) => ({ authoredAt, authorship });
const mkPackageOriginMaterial = (material) => ({ type: "package_origin_material", material });
const mkPackageOriginUnrecorded = () => ({
    type: "package_origin_unrecorded",
});
const mkPackagingProvenance = (invocationId, packagingRef, completedAt, packagedFrom) => ({
    invocationId,
    packagingRef,
    completedAt,
    packagedFrom,
});
const mkPromotedFromFileProvenance = (fromFileId, generator) => ({ fromFileId, generator });
const mkFile = (id, title, contentHash, mediaType, description, classification, phase, provenance) => ({
    type: "file",
    id,
    title,
    contentHash,
    mediaType,
    description,
    classification,
    phase,
    provenance,
});
const mkSource = (id, title, contentHash, classification, phase, provenance) => ({
    type: "source",
    id,
    title,
    contentHash,
    classification,
    phase,
    provenance,
});
const mkSourceBibliography = (title, authors, year) => year === undefined
    ? { type: "source_bibliography", title, authors }
    : { type: "source_bibliography", title, authors, year };
const mkChunkSummary = (path, title, summary, text) => ({ path, title, summary, text });
const mkSourceSummary = (superSummary, displayTitle, conversationTitleCandidate, chunks, bibliography) => ({
    superSummary,
    displayTitle,
    conversationTitleCandidate,
    chunks,
    ...(bibliography === undefined ? {} : { bibliography }),
});
const mkMaterial = (id, title, content, contentHash, classification, phase, templateRef, provenance, lineage) => ({
    type: "material",
    id,
    title,
    content,
    contentHash,
    classification,
    phase,
    templateRef,
    provenance,
    lineage,
});
const mkExport = (id, title, content, contentHash, classification, phase, templateRef, packagingProvenance) => ({
    type: "export",
    id,
    title,
    content,
    contentHash,
    classification,
    phase,
    templateRef,
    packagingProvenance,
});
const mkDeleted = (id, prior, deletedAt) => ({
    type: "deleted",
    id,
    prior,
    deletedAt,
});
const mkResourceState = (resources, checksumRegister) => ({ resources, checksumRegister });
const initial = {
    resources: new Map(),
    checksumRegister: new Map(),
};
//# sourceMappingURL=state.js.map